import { forwardRef } from 'react'
import {
  Link as RouterLink,
  Navigate as RouterNavigate,
  useLocation as useRouterLocation,
  useNavigate as useRouterNavigate,
  type LinkProps,
  type NavigateOptions,
  type NavigateProps,
  type To,
} from 'react-router-dom'
import { useHost } from '../host'

/**
 * react-router's navigation, for an application that may be mounted under a
 * host's route. Every screen imports these instead of react-router-dom's: the
 * same names and signatures, with the application's own absolute paths
 * (`/regions/…`) put under where it is mounted when the host's router is in
 * charge. With a router of its own, `basename` already does that, and these
 * pass straight through.
 */
function usePrefix() {
  const { basePath, router } = useHost()
  const prefix = router === 'host' ? basePath : ''
  return (to: To): To => {
    if (!prefix) return to
    if (typeof to === 'string') return to.startsWith('/') ? `${prefix}${to === '/' ? '' : to}` || '/' : to
    return to.pathname?.startsWith('/') ? { ...to, pathname: `${prefix}${to.pathname === '/' ? '' : to.pathname}` } : to
  }
}

/** The application's path for `to`: what goes in an href. */
export function useAppPath() {
  const prefix = usePrefix()
  return (to: string) => prefix(to) as string
}

export function useNavigate() {
  const navigate = useRouterNavigate()
  const prefix = usePrefix()
  return (to: To | number, options?: NavigateOptions) =>
    typeof to === 'number' ? navigate(to) : navigate(prefix(to), options)
}

/** The location within the application: under a host's route, without its prefix. */
export function useLocation() {
  const location = useRouterLocation()
  const { basePath, router } = useHost()
  if (router !== 'host' || !basePath || !location.pathname.startsWith(basePath)) return location
  return { ...location, pathname: location.pathname.slice(basePath.length) || '/' }
}

export function Navigate(props: NavigateProps) {
  const prefix = usePrefix()
  return <RouterNavigate {...props} to={prefix(props.to)} />
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const prefix = usePrefix()
  return <RouterLink ref={ref} {...props} to={prefix(props.to)} />
})

/**
 * The query that carries a check filter into a plot's screen, so the review
 * queue there is the group being reviewed rather than every issue in the run.
 */
export const inspectQuery = (checks: string[]) =>
  checks.length ? `?check=${encodeURIComponent(checks.join(','))}` : ''
